/**
 * RitualMediaSection Component
 *
 * Display and manage ritual media (images and videos) with full CRUD operations.
 * UI matches CommunityMediaSection with grid for images and list for videos.
 * Uses React Query hooks for real-time server synchronization.
 * Supports AI image generation and YouTube/Vimeo video URL embeds.
 */

import {
  ArrowUpToLine,
  GripVertical,
  Image,
  Music,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Video,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { CustomInput } from "@/components/forms/customInput"
import { AiImageGenerator } from "@/components/media/AiImageGenerator"
import { MediaUploadModal } from "@/components/media/MediaUploadModal"
import { MediaPreview } from "@/components/media/mediaPreview"
import { MEDIA_UPLOAD_LIMITS } from "@/constants/media"
import { markImageUsed } from "@/features/media/services/ai-image.service"
import { fetchVideoMetadata, isValidVideoUrl } from "@/utils/videoThumbnail"
import { useDeleteRitualImage } from "../hooks/useDeleteRitualImage"
import { useRitualImages } from "../hooks/useRitualImages"
import { useSetRitualAudioCover } from "../hooks/useSetRitualAudioCover"
import { useSetRitualCoverImage } from "../hooks/useSetRitualCoverImage"
import { useUploadRitualImage } from "../hooks/useUploadRitualImage"

interface EntityContext {
  name?: string
  description?: string
  type?: string
  tags?: string[]
}

interface Photo {
  id: string
  src: string
  isCover?: boolean
}

interface RitualVideoFile {
  id: string
  title: string
  url: string
}

interface VideoUrlEntry {
  id: string
  url: string
  thumbnail: string
  isEditing?: boolean
  title?: string
  isProcessed?: boolean
  isInvalid?: boolean
}

interface RitualMediaSectionProps {
  ritualId: string
  onMediaCountChange?: (imageCount: number, videoCount: number) => void
  onCoverImageChange?: (hasCoverImage: boolean) => void
  onVideoUrlsChange?: (videos: VideoUrlEntry[]) => void
  initialVideoUrls?: VideoUrlEntry[]
  showVideoUrlInput?: boolean
  showAudioSection?: boolean
  maxFiles?: number
  maxFileSize?: number
  maxAudioFileSize?: number
  enableAiGeneration?: boolean
  aiEntityType?: "ritual" | "community"
  aiEntityContext?: EntityContext
}

const RITUAL_MEDIA_LIMITS = {
  MAX_IMAGES: 5,
  MAX_AUDIO: 5,
  MAX_FILE_SIZE: 5 * 1024 * 1024,
  MAX_AUDIO_FILE_SIZE: 100 * 1024 * 1024,
}

interface AudioRowData {
  id: string
  fileName: string
  fileSizeBytes: number | null
  fileType: string | null
  posterUrl: string | null
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return "Audio"
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`
  return `${bytes}B`
}

export function RitualMediaSection({
  ritualId,
  onMediaCountChange,
  onCoverImageChange,
  onVideoUrlsChange,
  initialVideoUrls = [],
  showVideoUrlInput = false,
  showAudioSection = false,
  maxFiles = RITUAL_MEDIA_LIMITS.MAX_IMAGES,
  maxFileSize = RITUAL_MEDIA_LIMITS.MAX_FILE_SIZE,
  maxAudioFileSize = RITUAL_MEDIA_LIMITS.MAX_AUDIO_FILE_SIZE,
  enableAiGeneration = false,
  aiEntityType,
  aiEntityContext,
}: RitualMediaSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const thumbnailInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const audioPosterInputRef = useRef<HTMLInputElement>(null)
  const aiGenerationIdRef = useRef<string | null>(null)
  const debounceTimersRef = useRef<Record<number, NodeJS.Timeout>>({})
  const prevCountsRef = useRef({ images: 0, videos: 0 })
  const fetchedMetadataRef = useRef<Set<string>>(new Set())

  const { data: ritual, isLoading } = useRitualImages(ritualId)
  const uploadImage = useUploadRitualImage()
  const deleteImage = useDeleteRitualImage()
  const setCoverImage = useSetRitualCoverImage()
  const setAudioCover = useSetRitualAudioCover()
  const [isUploadingAudio, setIsUploadingAudio] = useState(false)
  const [audioError, setAudioError] = useState<string | null>(null)
  const [uploadingPosterId, setUploadingPosterId] = useState<string | null>(null)

  const [isUploading, setIsUploading] = useState(false)
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false)
  const [videoUrls, setVideoUrls] = useState<VideoUrlEntry[]>(
    initialVideoUrls.length > 0
      ? initialVideoUrls
      : [{ id: "video-0", url: "", thumbnail: "", title: "", isProcessed: false }],
  )
  const [urlErrors, setUrlErrors] = useState<Record<number, string | null>>({})
  const [thumbnailError, setThumbnailError] = useState<string | null>(null)
  const [uploadingThumbnailIndex, setUploadingThumbnailIndex] = useState<number | null>(null)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  const images: Photo[] = useMemo(() => {
    const rawImages = (ritual?.uploads || [])
      .filter((u: { fileType: string }) => u.fileType?.startsWith("image/"))
      .map((img: { id: string; filePath: string; isCover: boolean }) => ({
        id: img.id,
        src: img.filePath,
        isCover: img.isCover,
      }))

    const hasAnyCover = rawImages.some(img => img.isCover)
    if (!hasAnyCover && rawImages.length > 0) {
      rawImages[0] = { ...rawImages[0], isCover: true }
    }

    return rawImages
  }, [ritual?.uploads])

  const videos: RitualVideoFile[] = useMemo(
    () =>
      (ritual?.uploads || [])
        .filter((u: { fileType: string }) => u.fileType?.startsWith("video/"))
        .map((vid: { id: string; filePath: string; fileName: string }) => ({
          id: vid.id,
          title: vid.fileName || "Video",
          url: vid.filePath,
        })),
    [ritual?.uploads],
  )

  const audios: AudioRowData[] = useMemo(
    () =>
      (ritual?.uploads || [])
        .filter(u => u.fileType?.startsWith("audio/"))
        .map(u => ({
          id: u.id,
          fileName: u.fileName || "Audio",
          fileSizeBytes: u.fileSize ?? null,
          fileType: u.fileType ?? null,
          posterUrl: u.thumbnailPath ?? null,
        })),
    [ritual?.uploads],
  )

  useEffect(() => {
    if (ritual?.uploads) {
      const imageCount = ritual.uploads.filter((u: { fileType: string }) =>
        u.fileType?.startsWith("image/"),
      ).length
      const videoCount = ritual.uploads.filter((u: { fileType: string }) =>
        u.fileType?.startsWith("video/"),
      ).length

      if (
        imageCount !== prevCountsRef.current.images ||
        videoCount !== prevCountsRef.current.videos
      ) {
        prevCountsRef.current = { images: imageCount, videos: videoCount }
        onMediaCountChange?.(imageCount, videoCount)
      }

      const hasCover = images.some(img => img.isCover)
      onCoverImageChange?.(hasCover)
    }
  }, [ritual, images, onMediaCountChange, onCoverImageChange])

  const handleDelete = useCallback(
    (uploadId: string, type: "image" | "video") => {
      deleteImage.mutate(
        { ritualId, uploadId },
        {
          onSuccess: () => {
            toast.success(`${type === "image" ? "Image" : "Video"} deleted successfully`)
          },
          onError: () => {
            toast.error(`Failed to delete ${type === "image" ? "image" : "video"}`)
          },
        },
      )
    },
    [ritualId, deleteImage],
  )

  const handleAudioUploadClick = useCallback(() => {
    if (audios.length >= RITUAL_MEDIA_LIMITS.MAX_AUDIO) {
      toast.error(`Maximum ${RITUAL_MEDIA_LIMITS.MAX_AUDIO} audio files`)
      return
    }
    audioInputRef.current?.click()
  }, [audios.length])

  const handleAudioFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      if (!file.type.startsWith("audio/")) {
        setAudioError("Please select an audio file (MP3)")
        return
      }
      if (file.size > maxAudioFileSize) {
        setAudioError(`Audio file exceeds ${maxAudioFileSize / (1024 * 1024)}MB`)
        return
      }
      setAudioError(null)
      setIsUploadingAudio(true)
      try {
        await uploadImage.mutateAsync({ ritualId, file })
      } finally {
        setIsUploadingAudio(false)
        if (audioInputRef.current) {
          audioInputRef.current.value = ""
        }
      }
    },
    [ritualId, uploadImage, maxAudioFileSize],
  )

  const handleAudioDelete = useCallback(
    (uploadId: string) => {
      deleteImage.mutate(
        { ritualId, uploadId },
        {
          onSuccess: () => toast.success("Audio deleted"),
          onError: () => toast.error("Failed to delete audio"),
        },
      )
    },
    [ritualId, deleteImage],
  )

  const handleAudioPosterSelect = useCallback((uploadId: string) => {
    if (!audioPosterInputRef.current) return
    audioPosterInputRef.current.value = ""
    ;(
      audioPosterInputRef.current as HTMLInputElement & { dataset: { uploadId?: string } }
    ).dataset.uploadId = uploadId
    audioPosterInputRef.current.click()
  }, [])

  const handleAudioPosterChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      const uploadId = (e.target as HTMLInputElement & { dataset: { uploadId?: string } }).dataset
        .uploadId
      if (!file || !uploadId) return
      if (!file.type.startsWith("image/")) {
        toast.error("Audio cover must be an image")
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Audio cover must be less than 5MB")
        return
      }
      setUploadingPosterId(uploadId)
      try {
        await setAudioCover.mutateAsync({ ritualId, uploadId, file })
      } finally {
        setUploadingPosterId(null)
        if (audioPosterInputRef.current) {
          audioPosterInputRef.current.value = ""
        }
      }
    },
    [ritualId, setAudioCover],
  )

  const handleSetCover = useCallback(
    (uploadId: string) => {
      setCoverImage.mutate(
        { ritualId, uploadId },
        {
          onSuccess: () => {
            toast.success("Cover image updated")
          },
          onError: () => {
            toast.error("Failed to update cover image")
          },
        },
      )
    },
    [ritualId, setCoverImage],
  )

  const handleModalUpload = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        setIsUploading(true)
        await uploadImage.mutateAsync({ ritualId, file })
      }
      setIsUploading(false)
    },
    [ritualId, uploadImage],
  )

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files) return

      const filesArray = Array.from(files)

      const imageFiles = filesArray.filter(file => file.type.startsWith("image/"))
      const videoFiles = filesArray.filter(file => file.type.startsWith("video/"))

      if (images.length + imageFiles.length > maxFiles) {
        toast.error(`You can only upload a maximum of ${maxFiles} images`)
        return
      }

      if (videos.length + videoFiles.length > MEDIA_UPLOAD_LIMITS.MAX_VIDEOS) {
        toast.error(`You can only upload a maximum of ${MEDIA_UPLOAD_LIMITS.MAX_VIDEOS} videos`)
        return
      }

      filesArray.forEach((file: File) => {
        setIsUploading(true)
        uploadImage.mutate(
          { ritualId, file },
          {
            onSettled: () => {
              setIsUploading(false)
            },
          },
        )
      })

      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    },
    [ritualId, images.length, videos.length, uploadImage, maxFiles],
  )

  const handleAiImageSelected = useCallback(
    async (file: File, generationId: string, imageIndex: number) => {
      aiGenerationIdRef.current = generationId
      try {
        await uploadImage.mutateAsync({ ritualId, file })
        await markImageUsed(generationId, imageIndex)
      } catch (error) {
        console.error("Failed to upload AI image:", error)
      }
    },
    [ritualId, uploadImage],
  )

  const handleVideoUrlChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const newUrl = e.target.value

      if (debounceTimersRef.current[index]) {
        clearTimeout(debounceTimersRef.current[index])
      }

      setVideoUrls(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = { ...updated[index], url: newUrl }
        }
        return updated
      })

      if (newUrl && !isValidVideoUrl(newUrl)) {
        setUrlErrors(prev => ({ ...prev, [index]: "Please enter a valid YouTube or Vimeo URL" }))
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], isInvalid: true, isProcessed: true }
          }
          return updated
        })
        return
      }

      if (urlErrors[index]) {
        setUrlErrors(prev => ({ ...prev, [index]: null }))
      }

      if (!newUrl || isValidVideoUrl(newUrl)) {
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], isInvalid: false, isProcessed: false }
          }
          return updated
        })
      }

      if (isValidVideoUrl(newUrl)) {
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], isProcessed: true }
          }
          return updated
        })

        debounceTimersRef.current[index] = setTimeout(async () => {
          const { thumbnail, title } = await fetchVideoMetadata(newUrl)

          setVideoUrls(prev => {
            const updated = [...prev]
            if (updated[index]) {
              updated[index] = { ...updated[index], thumbnail, title }
            }
            if (onVideoUrlsChange) {
              onVideoUrlsChange(updated.filter(v => v.url))
            }
            return updated
          })
        }, 500)
      }
    },
    [onVideoUrlsChange, urlErrors],
  )

  const addVideoUrl = useCallback(() => {
    const newIndex = videoUrls.length
    setVideoUrls(prev => [
      ...prev,
      { id: `new-${newIndex}`, url: "", thumbnail: "", title: "", isProcessed: false },
    ])
  }, [videoUrls.length])

  const handleDeleteVideo = useCallback(
    (index: number) => {
      if (index === 0 && !videoUrls[0]?.url) {
        return
      }

      const updated = videoUrls.filter((_, i) => i !== index)
      setVideoUrls(updated)

      const newErrors: Record<number, string | null> = {}
      updated.forEach((_, i) => {
        if (urlErrors[i] !== undefined) {
          newErrors[i] = urlErrors[i]
        }
      })
      setUrlErrors(newErrors)

      if (onVideoUrlsChange) {
        onVideoUrlsChange(updated.filter(v => v.url))
      }
    },
    [videoUrls, urlErrors, onVideoUrlsChange],
  )

  const handleEditVideo = useCallback((index: number) => {
    setVideoUrls(prev =>
      prev.map((video, i) => (i === index ? { ...video, isEditing: true } : video)),
    )
  }, [])

  const handleFinishEditVideo = useCallback(
    async (index: number) => {
      const video = videoUrls[index]
      if (!video?.url) return

      if (debounceTimersRef.current[index]) {
        clearTimeout(debounceTimersRef.current[index])
      }

      const { thumbnail, title } = await fetchVideoMetadata(video.url)

      setVideoUrls(prev => {
        const updated = prev.map((v, i) =>
          i === index ? { ...v, thumbnail, title, isEditing: false, isProcessed: true } : v,
        )
        if (onVideoUrlsChange) {
          onVideoUrlsChange(updated.filter(v => v.url))
        }
        return updated
      })
    },
    [videoUrls, onVideoUrlsChange],
  )

  const handleVideoUrlBlur = useCallback(
    (index: number) => {
      handleFinishEditVideo(index)
    },
    [handleFinishEditVideo],
  )

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index)
  }, [])

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault()
      if (draggedIndex === null || draggedIndex === index) return

      const newVideoUrls = [...videoUrls]
      const draggedItem = newVideoUrls[draggedIndex]
      newVideoUrls.splice(draggedIndex, 1)
      newVideoUrls.splice(index, 0, draggedItem)

      setVideoUrls(newVideoUrls)
      setDraggedIndex(index)

      if (onVideoUrlsChange) {
        onVideoUrlsChange(newVideoUrls.filter(v => v.url))
      }
    },
    [draggedIndex, videoUrls, onVideoUrlsChange],
  )

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null)
  }, [])

  const handleThumbnailSelect = useCallback((index: number) => {
    thumbnailInputRef.current?.click()
    if (thumbnailInputRef.current) {
      ;(
        thumbnailInputRef.current as HTMLInputElement & { dataset: { videoIndex?: string } }
      ).dataset.videoIndex = index.toString()
    }
  }, [])

  const handleThumbnailChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const index = parseInt(
        (e.target as HTMLInputElement & { dataset: { videoIndex?: string } }).dataset.videoIndex ||
          "0",
        10,
      )

      if (!file.type.startsWith("image/")) {
        setThumbnailError("Please select an image file (JPEG, PNG, GIF, WebP)")
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        setThumbnailError("Thumbnail image must be less than 5MB")
        return
      }

      setThumbnailError(null)
      setUploadingThumbnailIndex(index)

      try {
        const localThumbnailUrl = URL.createObjectURL(file)
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail: localThumbnailUrl }
          }
          if (onVideoUrlsChange) {
            onVideoUrlsChange(updated.filter(v => v.url))
          }
          return updated
        })
      } catch {
        setThumbnailError("Failed to upload thumbnail. Please try again.")
      } finally {
        setUploadingThumbnailIndex(null)
      }

      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = ""
      }
    },
    [onVideoUrlsChange],
  )

  useEffect(() => {
    if (!hasSyncedInitialUrls.current && initialVideoUrls && initialVideoUrls.length > 0) {
      setVideoUrls(initialVideoUrls)
      hasSyncedInitialUrls.current = true
    }
  }, [initialVideoUrls, initialVideoUrls.length])

  const hasSyncedInitialUrls = useRef(false)

  useEffect(() => {
    videoUrls.forEach(async (video, index) => {
      if (
        !video.url ||
        video.thumbnail ||
        fetchedMetadataRef.current.has(video.id) ||
        video.isInvalid
      ) {
        return
      }

      if (isValidVideoUrl(video.url)) {
        fetchedMetadataRef.current.add(video.id)

        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], isProcessed: true }
          }
          return updated
        })

        const { thumbnail, title } = await fetchVideoMetadata(video.url)

        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail, title }
          }
          if (onVideoUrlsChange) {
            onVideoUrlsChange(updated.filter(v => v.url))
          }
          return updated
        })
      }
    })
  }, [videoUrls, onVideoUrlsChange])

  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(timer => {
        clearTimeout(timer)
      })
    }
  }, [])

  useEffect(() => {
    videoUrls.forEach((video, index) => {
      if (video.isEditing) {
        const input = document.getElementById(`videoUrl-${index}`)
        if (input) {
          input.focus()
        }
      }
    })
  }, [videoUrls])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <p className="text-body">Loading media...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={handleFileUpload}
        className="hidden"
        disabled={isUploading}
      />
      <input
        ref={thumbnailInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleThumbnailChange}
        className="hidden"
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/mpeg,audio/mp3"
        onChange={handleAudioFileChange}
        className="hidden"
      />
      <input
        ref={audioPosterInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleAudioPosterChange}
        className="hidden"
      />

      <div className="flex items-center gap-5">
        <button
          type="button"
          onClick={() => {
            if (images.length >= maxFiles) {
              toast.error(`Maximum ${maxFiles} images already uploaded`)
              return
            }
            setUploadModalOpen(true)
          }}
          className="flex justify-between items-center w-full border border-dashed border-brand-slate-alpha-70 rounded-[15px] p-2.5 pr-5 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex justify-center items-center gap-2">
            <div className="bg-brand-slate-alpha-70 p-2.5 flex justify-center items-center rounded-full">
              <ArrowUpToLine className="w-5 h-5 text-white" />
            </div>
            <p className="font-semibold text-primary text-sm">Upload Media</p>
          </div>

          <p className="text-xs text-primary leading-5">
            Up to {maxFiles} files. PNG, JPG up to {maxFileSize / (1024 * 1024)}MB
          </p>
        </button>

        {enableAiGeneration && aiEntityType && images.length < maxFiles && (
          <button
            type="button"
            onClick={() => setAiGeneratorOpen(true)}
            className="flex items-center gap-2.5 border border-brand-slate-alpha-70 rounded-full px-6.75 py-3 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors"
          >
            <Sparkles className="w-6 h-6 text-white" />
            <p className="font-semibold text-primary text-sm uppercase text-nowrap">
              Generate Image
            </p>
          </button>
        )}
      </div>

      {thumbnailError && (
        <p className="text-xs text-brand-terracotta-bright mt-1">{thumbnailError}</p>
      )}

      <div className="mt-2">
        <h3 className="font-bold text-body mb-3">Images</h3>
        {images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {images.map(image => (
              <MediaPreview
                key={image.id}
                type="image"
                src={image.src}
                isCover={image.isCover}
                onCoverSelect={() => handleSetCover(image.id)}
                onRemove={() => handleDelete(image.id, "image")}
              />
            ))}
          </div>
        )}
      </div>

      {enableAiGeneration && aiEntityType && (
        <AiImageGenerator
          open={aiGeneratorOpen}
          onOpenChange={setAiGeneratorOpen}
          entityType={aiEntityType}
          entityContext={aiEntityContext}
          onImageSelected={handleAiImageSelected}
          maxFiles={maxFiles}
        />
      )}

      {showVideoUrlInput && (
        <div className="mt-3">
          <h3 className="font-bold text-primary mb-5">Videos</h3>

          <div className="space-y-5">
            {videoUrls.map((video, index) => {
              const showInput =
                !video.url || video.isEditing || !video.isProcessed || video.isInvalid
              const showThumbnail =
                video.url && video.isProcessed && !video.isEditing && !video.isInvalid

              return (
                <div
                  key={video.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={e => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-start gap-2 ${draggedIndex === index ? "opacity-50" : ""}`}
                >
                  <div
                    className="mt-4 p-2 text-body cursor-grab active:cursor-grabbing hover:text-primary transition-colors"
                    title="Drag to reorder"
                  >
                    <GripVertical className="w-5 h-5" />
                  </div>

                  {showInput && (
                    <>
                      <div className="flex-1">
                        <CustomInput
                          id={`videoUrl-${index}`}
                          label={"Embed YouTube or Vimeo video"}
                          value={video.url}
                          onChange={e => handleVideoUrlChange(e, index)}
                          onBlur={() => handleVideoUrlBlur(index)}
                          placeholder="Enter YouTube or Vimeo URL"
                          error={urlErrors[index] || undefined}
                          onClearError={() => setUrlErrors(prev => ({ ...prev, [index]: null }))}
                        />
                      </div>
                      {(index > 0 || video.url) && (
                        <button
                          type="button"
                          onClick={() => handleDeleteVideo(index)}
                          className="mt-6 p-2 text-white cursor-pointer"
                          title="Remove video"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </>
                  )}

                  {showThumbnail && (
                    <>
                      <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-base flex items-center justify-center">
                        {video.thumbnail ? (
                          <img
                            src={video.thumbnail}
                            alt="Video thumbnail"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Video className="w-10 h-10 text-body" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 py-1 h-[75px] flex flex-col items-start justify-center">
                        {video.title ? (
                          <p className="text-sm font-medium text-primary truncate overflow-hidden md:w-[500px] w-[200px]">
                            {video.title}
                          </p>
                        ) : null}
                        <p className="text-xs text-body truncate overflow-hidden md:w-[500px] w-[200px]">
                          {video.url}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleEditVideo(index)}
                        className="mt-4 p-2 text-primary hover:text-primary/80 cursor-pointer"
                        title="Edit video"
                      >
                        <Pencil className="w-4.5 h-4.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleThumbnailSelect(index)}
                        className="mt-4 p-2 text-primary hover:text-primary/80 cursor-pointer relative"
                        title="Upload custom thumbnail"
                        disabled={uploadingThumbnailIndex === index}
                      >
                        {uploadingThumbnailIndex === index ? (
                          <div className="w-4.5 h-4.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Image className="w-4.5 h-4.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteVideo(index)}
                        className="mt-4 p-2 text-white cursor-pointer"
                        title="Remove video"
                      >
                        <Trash2 className="w-4.5 h-4.5" />
                      </button>
                    </>
                  )}
                </div>
              )
            })}

            <button
              type="button"
              onClick={addVideoUrl}
              className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium"
            >
              <Plus className="w-5 h-5" />
              Add More
            </button>
          </div>
        </div>
      )}

      {showAudioSection && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-primary">Audio</h3>
            <button
              type="button"
              onClick={handleAudioUploadClick}
              disabled={isUploadingAudio || audios.length >= RITUAL_MEDIA_LIMITS.MAX_AUDIO}
              className="flex items-center gap-1.5 text-primary hover:text-primary/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
              {isUploadingAudio ? "Uploading…" : "Add audio"}
            </button>
          </div>

          {audioError && <p className="text-xs text-brand-terracotta-bright mb-3">{audioError}</p>}

          <div className="space-y-5">
            {audios.length === 0 ? (
              <p className="text-xs text-body">
                No audio yet. Upload an MP3 (up to {maxAudioFileSize / (1024 * 1024)}MB).
              </p>
            ) : (
              audios.map(audio => (
                <div key={audio.id} className="flex items-start gap-2">
                  <div
                    className="mt-4 p-2 text-body cursor-grab active:cursor-grabbing hover:text-primary transition-colors"
                    title="Drag to reorder"
                  >
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-base flex items-center justify-center">
                    {audio.posterUrl ? (
                      <img
                        src={audio.posterUrl}
                        alt="Audio cover"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Music className="w-10 h-10 text-body" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 py-1 h-[75px] flex flex-col items-start justify-center">
                    <p className="text-sm font-medium text-primary truncate overflow-hidden md:w-[500px] w-[200px]">
                      {audio.fileName}
                    </p>
                    <p className="text-xs text-body">
                      {audio.fileType === "audio/mpeg" || audio.fileType === "audio/mp3"
                        ? "MP3"
                        : "Audio"}
                      {audio.fileSizeBytes !== null
                        ? `, ${formatFileSize(audio.fileSizeBytes)}`
                        : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAudioPosterSelect(audio.id)}
                    disabled={uploadingPosterId === audio.id}
                    className="mt-4 p-2 text-primary hover:text-primary/80 cursor-pointer disabled:opacity-50"
                    title={audio.posterUrl ? "Replace cover image" : "Upload cover image"}
                  >
                    {uploadingPosterId === audio.id ? (
                      <div className="w-4.5 h-4.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Image className="w-4.5 h-4.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAudioDelete(audio.id)}
                    className="mt-4 p-2 text-white cursor-pointer"
                    title="Remove audio"
                  >
                    <Trash2 className="w-4.5 h-4.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <MediaUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onUpload={handleModalUpload}
        maxFiles={maxFiles}
        maxFileSize={maxFileSize}
        allowedTypes={["image/png", "image/jpeg", "image/jpg"]}
        title="Upload Media"
        isUploading={isUploading}
        existingFilesCount={images.length}
      />
    </div>
  )
}
